<template>
  <div v-if="lesson" class="page stack">
    <p class="eyebrow">Lesson {{ lesson.number }} · {{ lesson.dish }}</p>

    <template v-if="!summary">
      <h1>What did everyone think?</h1>
      <p class="muted">Tap the mic on the keyboard and let each person say their piece, one after the other. Names help (“Carrie thought…”). Nothing is too small: the sauce, the timing, whether it was worth it.</p>
      <p v-if="lesson.debriefPrompt" class="prompt">{{ lesson.debriefPrompt }}</p>
      <textarea v-model="transcript" class="talk" rows="9" placeholder="Matt: honestly better than I expected, the crust was…" :disabled="busy"></textarea>
      <div class="row">
        <button type="button" class="btn btn-primary btn-lg" :disabled="busy || transcript.trim().length < 10" @click="submit">{{ busy ? 'Listening…' : 'Done talking' }}</button>
        <router-link to="/" class="btn">Later</router-link>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
      <p class="muted small">Saved as you type. The summary goes into the course; the transcript stays with it.</p>
    </template>

    <template v-else>
      <h1>{{ summary.verdict }}</h1>
      <section v-if="summary.people?.length" class="card">
        <p class="eyebrow">Who said what</p>
        <ul class="plain-list">
          <li v-for="p in summary.people" :key="p.name"><strong>{{ p.name }}:</strong> {{ p.take }}</li>
        </ul>
      </section>
      <section v-if="summary.worked?.length" class="card">
        <p class="eyebrow">What worked</p>
        <ul class="plain-list bullets"><li v-for="(w, i) in summary.worked" :key="i">{{ w }}</li></ul>
      </section>
      <section v-if="summary.change?.length" class="card">
        <p class="eyebrow">What to change</p>
        <ul class="plain-list bullets"><li v-for="(c, i) in summary.change" :key="i">{{ c }}</li></ul>
      </section>
      <section v-if="summary.nextTime" class="card">
        <p class="eyebrow">For the next lesson</p>
        <p>{{ summary.nextTime }}</p>
      </section>
      <p class="muted small">Worth it: {{ summary.worthIt }} of 5 · {{ summary.again ? 'Would make it again' : 'Not asking for it again' }}</p>
      <router-link to="/" class="btn btn-primary btn-lg">Back to the course</router-link>
    </template>
  </div>
</template>

<script>
// One button after dinner. Everyone talks; the app captures, summarises,
// and the next lesson reads the summary. A rating would have been a
// number nobody argues with; a conversation is the actual review.
import { computed, ref, watch } from 'vue';
import { lessonById, LESSONS } from '../curriculum/lessons.js';
import { readDraft, writeDraft, clearDraft } from '../utils/talkDraft.js';
import { summarizeDebrief } from '../utils/api.js';
import { loadProgress, markLesson, debriefHistory } from '../utils/course.js';
import { setActivity } from '../state.js';

export default {
  name: 'DebriefScreen',
  props: { id: { type: String, required: true }, hat: { type: String, default: null } },
  setup (props) {
    const lesson = computed(() => lessonById(props.id));
    const transcript = ref(readDraft(props.id));
    const summary = ref(null);
    const busy = ref(false);
    const error = ref(null);
    const progress = ref({});
    setActivity('debrief');

    watch(transcript, (text) => writeDraft(props.id, text));
    watch(() => props.hat, async (hat) => {
      progress.value = hat ? await loadProgress(hat) : {};
      const existing = progress.value[props.id]?.debrief?.summary;
      if (existing) summary.value = existing;
    }, { immediate: true });

    async function submit () {
      busy.value = true;
      error.value = null;
      try {
        const previous = debriefHistory(progress.value, LESSONS).filter((d) => d.lessonId !== props.id).slice(-3);
        const result = await summarizeDebrief({ lesson: lesson.value, transcript: transcript.value, previous });
        summary.value = result;
        const fields = { debrief: { transcript: transcript.value, summary: result, at: Date.now() } };
        if (!progress.value[props.id]?.cookedAt) fields.cookedAt = Date.now();
        await markLesson(props.hat, props.id, fields);
        clearDraft(props.id);
      } catch (err) {
        error.value = err.message;
      } finally {
        busy.value = false;
      }
    }

    return { lesson, transcript, summary, busy, error, submit };
  }
};
</script>

<style scoped>
.prompt { font-style: italic; color: var(--muted); }
.error { color: var(--danger); }
.small { font-size: 0.85rem; }
.plain-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.plain-list.bullets { list-style: disc; padding-left: 1.2rem; }
</style>
